import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, ensureRegistered, fetchMe, type MeResponse } from '@/lib/api';
import { track } from '@/lib/analytics';
import { useTrialActivation } from '@/hooks/useTrialActivation';
import { BottomTabs, type TabId } from '@/components/BottomTabs';
import { Toast } from '@/components/Toast';
import { HomeScreen } from '@/screens/HomeScreen';
import { PaywallScreen } from '@/screens/PaywallScreen';
import { SuccessScreen } from '@/screens/SuccessScreen';
import { LoadingScreen } from '@/screens/LoadingScreen';
import { ErrorScreen } from '@/screens/ErrorScreen';
import { OnboardingScreen, ONBOARDING_COMPLETE_KEY } from '@/screens/OnboardingScreen';
import { ProfileScreen } from '@/screens/ProfileScreen';
import { SupportScreen } from '@/screens/SupportScreen';
import { SetupScreen } from '@/screens/setup/SetupScreen';
import { cloudGet, getWebApp } from '@/lib/telegram';

type Screen = TabId | 'paywall' | 'success' | 'onboarding';

const TABBED_SCREENS: Screen[] = ['home', 'setup', 'account', 'help'];
const BOT_DEEPLINK = 'https://t.me/shushvpnbot?start=miniapp';

export function App() {
  const [screen, setScreen] = useState<Screen>('home');
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loadState, setLoadState] = useState<'loading' | 'ok' | 'error'>('loading');
  const [errorKind, setErrorKind] = useState<'auth' | 'network' | 'other'>('other');
  const [toast, setToast] = useState<string | null>(null);
  // Track which payment method completed so SuccessScreen can render the
  // matching receipt. null until a payment succeeds.
  const [paidMethod, setPaidMethod] = useState<'stars' | 'ton' | null>(null);
  // Lets profile/help jump straight into the setup flow's "other device"
  // sub-screen instead of the cover. Reset to undefined when leaving setup
  // so a subsequent tab tap lands on the cover as usual.
  const [setupInitialStep, setSetupInitialStep] = useState<'other' | undefined>(undefined);
  // First-launch onboarding only runs once. We resolve the gate from
  // CloudStorage before deciding the initial screen so the bottom tabs
  // don't flash in for a frame on a brand-new user.
  const [onboardingResolved, setOnboardingResolved] = useState(false);
  // Cancels the post-payment background poller below if the user leaves
  // the mini app (or navigates back to a different screen) before the
  // subscription activates. Without this the while-loop keeps firing
  // fetchMe + setMe long after the component has unmounted.
  const paidPollAbort = useRef<AbortController | null>(null);
  // miniapp_opened fires exactly once per app instance (not per /me refresh).
  // Tracked here so a post-payment refetch or onboarding load doesn't double-
  // count the session in the dashboard's DAU tile.
  const openedTracked = useRef(false);

  const handleTrialReady = useCallback((fresh: MeResponse) => {
    setMe(fresh);
    setScreen('setup');
  }, []);
  const trial = useTrialActivation(screen === 'home', handleTrialReady, setMe);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast((cur) => (cur === msg ? null : cur)), 1800);
  }, []);

  const load = useCallback(async () => {
    setLoadState('loading');
    try {
      await ensureRegistered();
      const data = await fetchMe();
      setMe(data);
      setLoadState('ok');
      if (!openedTracked.current) {
        openedTracked.current = true;
        track('miniapp_opened', {
          subscription_state: data.subscription.state,
          had_trial: data.history.had_trial,
          had_paid: data.history.had_paid,
        });
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) setErrorKind('auth');
        else setErrorKind('other');
      } else {
        setErrorKind('network');
      }
      setLoadState('error');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // First-launch gate: show OnboardingScreen exactly once for brand-new users
  // (subscription.state === 'new' AND no onboardingComplete flag in
  // CloudStorage). Resolved only after /me lands so we know the user state.
  // Returning users — anyone who's ever had a trial or paid sub, or anyone
  // who's already finished onboarding — skip straight to the home screen.
  useEffect(() => {
    if (loadState !== 'ok' || !me || onboardingResolved) return;
    let cancelled = false;
    (async () => {
      if (me.subscription.state !== 'new') {
        if (!cancelled) setOnboardingResolved(true);
        return;
      }
      const done = await cloudGet(ONBOARDING_COMPLETE_KEY);
      if (cancelled) return;
      if (done !== '1') setScreen('onboarding');
      setOnboardingResolved(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [loadState, me, onboardingResolved]);

  const showTabs = useMemo(
    () => TABBED_SCREENS.includes(screen as TabId) && onboardingResolved,
    [screen, onboardingResolved],
  );

  const navigate = useCallback((next: Screen) => {
    setScreen(next);
  }, []);

  const onTab = useCallback((id: TabId) => {
    setScreen(id);
  }, []);

  // Telegram's BackButton mirrors paywall/success the same way the setup
  // flow does — visible only on off-tab screens, pops back to home. The
  // bottom tabs themselves are already hidden on these screens by showTabs.
  useEffect(() => {
    const back = getWebApp()?.BackButton;
    if (!back) return;
    if (screen === 'paywall' || screen === 'success') {
      const handler = () => setScreen('home');
      back.show();
      back.onClick(handler);
      return () => {
        back.offClick(handler);
        back.hide();
      };
    }
    back.hide();
    return undefined;
  }, [screen]);

  // After a payment lands, jump to the success screen immediately so the
  // user gets the receipt. The mini-app then polls /me in the background so
  // the subscription URL / expires_at can fill in as Core finishes
  // provisioning the VLESS key (Stars: a few seconds via Redis Stream → XRay;
  // TON: already active by the time the paywall handed off here). The
  // AbortController lets the App unmount effect (or a re-entry into
  // handlePaid) cancel the loop cleanly; without it the loop would keep
  // hitting fetchMe + setMe long after the user closed the mini app.
  const handlePaid = useCallback(
    async (method: 'stars' | 'ton') => {
      setPaidMethod(method);
      setScreen('success');

      paidPollAbort.current?.abort();
      const ctrl = new AbortController();
      paidPollAbort.current = ctrl;
      const { signal } = ctrl;

      const start = Date.now();
      while (!signal.aborted && Date.now() - start < 30_000) {
        try {
          const fresh = await fetchMe();
          if (signal.aborted) return;
          setMe(fresh);
          if (fresh.subscription.state === 'active' || fresh.subscription.state === 'trial') {
            return;
          }
        } catch {
          // ignore transient errors and keep polling
        }
        await new Promise<void>((resolve) => {
          const t = window.setTimeout(resolve, 1500);
          signal.addEventListener('abort', () => {
            window.clearTimeout(t);
            resolve();
          });
        });
      }
    },
    [],
  );

  // Abort the post-payment poll if the App itself unmounts (the mini app
  // window was closed or navigated away from). The PaywallScreen handles
  // its own poll cleanup, but the App-level poll started inside handlePaid
  // would otherwise outlive any UI.
  useEffect(() => () => paidPollAbort.current?.abort(), []);

  const openBot = useCallback(() => {
    const wa = getWebApp();
    if (wa?.openTelegramLink) {
      wa.openTelegramLink(BOT_DEEPLINK);
    } else {
      window.location.href = BOT_DEEPLINK;
    }
  }, []);

  let content: JSX.Element;
  // Hold on the loading screen until the onboarding gate has resolved.
  // Without this, a brand-new user would see the home screen render for one
  // frame before the cloudGet check redirects them to onboarding.
  if (loadState === 'loading' || (loadState === 'ok' && !onboardingResolved)) {
    content = <LoadingScreen />;
  } else if (loadState === 'error') {
    if (errorKind === 'auth') {
      content = (
        <ErrorScreen
          title="сессия не подтвердилась"
          body="открой мини-приложение из чата с @shushvpnbot — браузер не видит подпись telegram."
          cta={{ label: 'открыть в telegram', onClick: openBot }}
        />
      );
    } else {
      content = (
        <ErrorScreen
          title="не получилось загрузить"
          body="что-то с сетью или сервером. попробуй ещё раз."
          onRetry={() => void load()}
        />
      );
    }
  } else if (me) {
    if (screen === 'onboarding') {
      content = (
        <OnboardingScreen
          onFinish={({ gotoPaywall, startedTrial }) => {
            if (startedTrial) void load();
            navigate(gotoPaywall ? 'paywall' : 'home');
          }}
          onError={(msg) => showToast(msg)}
        />
      );
    } else if (screen === 'home') {
      content = (
        <HomeScreen
          me={me}
          trial={trial}
          navigate={(target) => navigate(target)}
          onCopy={() => showToast('ключ скопирован')}
        />
      );
    } else if (screen === 'setup') {
      content = (
        <SetupScreen
          me={me}
          initialStep={setupInitialStep}
          onCopy={() => showToast('ссылка скопирована')}
          onFinish={() => {
            setSetupInitialStep(undefined);
            navigate('home');
          }}
        />
      );
    } else if (screen === 'account') {
      content = (
        <ProfileScreen
          me={me}
          onInstallOtherDevice={() => {
            setSetupInitialStep('other');
            navigate('setup');
          }}
          onCopy={() => showToast('ссылка скопирована')}
        />
      );
    } else if (screen === 'help') {
      content = (
        <SupportScreen
          me={me}
          onInstallOtherDevice={() => {
            setSetupInitialStep('other');
            navigate('setup');
          }}
        />
      );
    } else if (screen === 'paywall') {
      content = (
        <PaywallScreen
          me={me}
          onPaid={(method) => void handlePaid(method)}
          onAlreadyActive={() => {
            showToast('подписка уже активна');
            navigate('home');
          }}
          onError={(msg) => showToast(msg)}
          onBack={() => navigate('home')}
        />
      );
    } else if (screen === 'success') {
      content = (
        <SuccessScreen
          me={me}
          method={paidMethod ?? 'stars'}
          onInstall={() => navigate('setup')}
          onHome={() => navigate('home')}
        />
      );
    } else {
      // Unknown screen — fall back to home rather than rendering a stub.
      // The switch is exhaustive against the Screen union now that all
      // surfaces are real; this branch only triggers if a future Screen
      // value gets added without wiring.
      content = (
        <HomeScreen
          me={me}
          trial={trial}
          navigate={(target) => navigate(target)}
          onCopy={() => showToast('ключ скопирован')}
        />
      );
    }
  } else {
    content = <LoadingScreen />;
  }

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        background: 'var(--paper)',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        key={screen}
        style={{
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
          animation: 'shushFade 280ms cubic-bezier(.2,.8,.2,1)',
        }}
      >
        {content}
      </div>

      {showTabs && <BottomTabs active={screen as TabId} onChange={onTab} />}

      {toast && <Toast message={toast} />}
    </div>
  );
}
